import { Box, Typography } from '@mui/material';
import React, { useEffect } from 'react';

import { useInstallationGuide } from '../../../hooks/useInstallationGuide';
import { GuidanceSection } from '../GuidanceSection';
import { WizardNavigation } from '../WizardNavigation';

/** Path to the power port image */
const POWER_PORT_PATH = '/assets/images/installation-guide/power-port.png';

/** Path to the outlet image */
const OUTLET_PATH = '/assets/images/installation-guide/outlet.png';

/**
 * ConnectPowerStep - Slide 2.8: Connect Power
 *
 * Shows instructions for connecting power to the Core:
 * - Image of Core power port with arrow indicator
 * - Image of wall outlet with plug
 * - Chatbot help button for power cable length issues
 */
export const ConnectPowerStep: React.FC = () => {
  const { goToStep, setMainStepId } = useInstallationGuide();

  useEffect(() => {
    setMainStepId('step_connectCoreToPower');
  }, [setMainStepId]);

  const handleConnected = () => {
    goToStep('confirm-power');
  };

  const guidanceButtons = [
    { label: __("My power cable can't reach the outlet"), flowId: 'power-cable-too-short' },
  ];

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h3" component="h1" sx={{ mb: 2, textAlign: 'center' }}>
        {__('Connect the Core to power.')}
      </Typography>

      {/* First instruction + image */}
      <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
        {__('Take the included ')}<strong>{__('power adapter')}</strong>{__(" and plug it into your Core's power port.")}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 3,
        }}
      >
        <Box
          component="img"
          src={POWER_PORT_PATH}
          alt={__('Core power port')}
          sx={{
            maxWidth: '100%',
            maxHeight: 140,
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Second instruction + image */}
      <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
        {__('Then plug the other end into a working outlet.')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 2,
        }}
      >
        <Box
          component="img"
          src={OUTLET_PATH}
          alt={__('Power outlet')}
          sx={{
            maxWidth: '100%',
            maxHeight: 140,
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Guidance section with chatbot button */}
      <GuidanceSection
        buttons={guidanceButtons}
      />

      <WizardNavigation
        primaryLabel={__('My Core is connected to power')}
        onPrimary={handleConnected}
      />
    </Box>
  );
};
